<template>
  <div
    class="upsi-row-actions"
    :data-testid="`bookclosure-project-list__upsi-actions--${index}`"
  >
    <v-btn
      v-if="showPublish"
      small
      text
      class="cycle-table-row__cycle-action text-none"
      :data-testid="`bookclosure-project-list__upsi-publish-btn--${index}`"
      @click.stop="onPublish"
    >
      <span class="cycle-table-row__cycle-action-text">Publish</span>
    </v-btn>

    <v-tooltip
      v-if="showReview"
      bottom
      content-class="tooltip-bottom"
      open-delay="300"
    >
      <template v-slot:activator="{ on, attrs }">
        <span
          class="upsi-row-actions__tooltip"
          v-bind="attrs"
          :data-testid="`bookclosure-project-list__upsi-review-tooltip--${index}`"
          v-on="on"
        >
          <v-btn
            small
            text
            class="cycle-table-row__cycle-action text-none"
            :data-testid="`bookclosure-project-list__upsi-review-btn--${index}`"
            @click.stop="onReview"
          >
            <span class="cycle-table-row__cycle-action-text">Review</span>
          </v-btn>
        </span>
      </template>
      <span>{{ reviewTooltip }}</span>
    </v-tooltip>

    <v-tooltip
      v-if="showReopen"
      bottom
      content-class="tooltip-bottom"
      open-delay="300"
    >
      <template v-slot:activator="{ on, attrs }">
        <span
          class="upsi-row-actions__tooltip"
          v-bind="attrs"
          v-on="on"
        >
          <button
            type="button"
            class="upsi-row-actions__reopen"
            :aria-label="reopenTooltip"
            :data-testid="`bookclosure-project-list__upsi-reopen-icon-btn--${index}`"
            @click.stop="onReopen"
          >
            <v-icon class="upsi-row-actions__reopen-icon" aria-hidden="true">mdi-lock-open-outline</v-icon>
          </button>
        </span>
      </template>
      <span>{{ reopenTooltip }}</span>
    </v-tooltip>
  </div>
</template>

<script>
import './UpsiRowActions.scss';
import {
  UPSI_STAGES,
  UPSI_TOOLTIPS,
} from '../../../../utils/constants';
import { isCycleClosed } from '../../../../utils/helpers';
import { isUpsiAwaitingPublisher } from '../../utils/cyclesHelpers';

const PUBLISH_MODE_REQUEST = 'request';
const PUBLISH_MODE_REPUBLISH = 'republish';

/**
 * Publish, Review, and the reopen icon for one cycles-table row.
 * The awaiting publisher keeps the labeled Reopen Cycle button on the row instead.
 * Review and Publish render only when this user can perform them.
 */
export default {
  name: 'UpsiRowActions',

  props: {
    cycle: {
      type: Object,
      required: true,
    },
    canPublishRight: {
      type: Boolean,
      default: false,
    },
    canReviewRight: {
      type: Boolean,
      default: false,
    },
    canReopen: {
      type: Boolean,
      default: false,
    },
    index: {
      type: Number,
      default: 0,
    },
  },

  computed: {
    stage() {
      const value = this.cycle && this.cycle.upsi_state ? this.cycle.upsi_state.stage : UPSI_STAGES.UNPUBLISHED;
      const numeric = Number(value);
      return Number.isNaN(numeric) ? UPSI_STAGES.UNPUBLISHED : numeric;
    },
    perms() {
      return (this.cycle && this.cycle.upsi_permissions) || {};
    },
    cycleClosed() {
      return isCycleClosed(this.cycle && this.cycle.stage);
    },
    awaitingPublisher() {
      return isUpsiAwaitingPublisher(this.cycle);
    },
    showPublish() {
      if (!this.cycleClosed) return false;
      const requestable = this.stage === UPSI_STAGES.UNPUBLISHED || this.stage === UPSI_STAGES.REJECTED;
      return requestable && !!this.perms.can_request && this.canPublishRight;
    },
    showReview() {
      if (!this.cycleClosed || this.awaitingPublisher) return false;
      return this.stage === UPSI_STAGES.PENDING_APPROVAL
        && !!this.perms.can_review
        && this.canReviewRight;
    },
    reviewTooltip() {
      return UPSI_TOOLTIPS.REVIEW;
    },
    showReopen() {
      if (!this.canReopen || this.awaitingPublisher) return false;
      if (this.showPublish) return true;
      return this.stage === UPSI_STAGES.PENDING_APPROVAL;
    },
    reopenTooltip() {
      return UPSI_TOOLTIPS.REOPEN;
    },
    publishMode() {
      return this.stage === UPSI_STAGES.REJECTED ? PUBLISH_MODE_REPUBLISH : PUBLISH_MODE_REQUEST;
    },
  },

  methods: {
    onPublish() {
      console.log('[UpsiRowActions]:', { index: this.index, mode: this.publishMode });
      this.$emit('publish', this.publishMode);
    },
    onReview() {
      console.log('[UpsiRowActions]:', { index: this.index, action: 'review' });
      this.$emit('review');
    },
    onReopen() {
      console.log('[UpsiRowActions]:', { index: this.index, action: 'reopen-cycle' });
      this.$emit('reopen-cycle');
    },
  },
};
</script>
